import React, { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { cn } from '@/lib/utils'
import type { CalendarEvent } from '@/hooks/useCalendarEvents'

interface CalendarViewProps {
  events: CalendarEvent[]
  selectedDate: Date
  onDateSelect: (date: Date) => void
  onMonthChange: (date: Date) => void
  loading?: boolean
}

const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  selectedDate,
  onDateSelect,
  onMonthChange,
  loading = false
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date())

  useEffect(() => {
    onMonthChange(currentMonth)
  }, [currentMonth, onMonthChange])

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1))
  }

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1))
  }

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return events.filter(event => {
      const eventDate = event.start.split('T')[0]
      return eventDate === dateStr
    })
  }

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between mb-4 px-2">
        <button
          onClick={handlePrevMonth}
          className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-neutral-400" />
        </button>
        <h2 className="text-lg font-semibold text-neutral-50 capitalize">
          {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
        </h2>
        <button
          onClick={handleNextMonth}
          className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-neutral-400" />
        </button>
      </div>
    )
  }

  const renderDaysOfWeek = () => {
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']
    return (
      <div className="grid grid-cols-7 mb-2">
        {days.map(day => (
          <div
            key={day}
            className="text-center text-xs font-medium text-neutral-500 py-2"
          >
            {day}
          </div>
        ))}
      </div>
    )
  }

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart)
    const endDate = endOfWeek(monthEnd)

    const rows: React.ReactNode[] = []
    let days: React.ReactNode[] = []
    let day = startDate

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day
        const dayEvents = getEventsForDate(day)
        const hasEvents = dayEvents.length > 0
        const isCurrentMonth = isSameMonth(day, currentMonth)
        const isSelected = isSameDay(day, selectedDate)
        const isTodayDate = isToday(day)

        days.push(
          <button
            key={day.toString()}
            onClick={() => onDateSelect(cloneDay)}
            className={cn(
              'relative aspect-square p-1 flex flex-col items-center justify-start rounded-lg transition-colors',
              !isCurrentMonth && 'opacity-30',
              isSelected && 'bg-emerald-600/20 border border-emerald-500',
              !isSelected && 'hover:bg-neutral-800',
              isTodayDate && !isSelected && 'bg-neutral-800'
            )}
          >
            <span
              className={cn(
                'text-sm font-medium',
                isSelected && 'text-emerald-400',
                !isSelected && isTodayDate && 'text-emerald-400',
                !isSelected && !isTodayDate && 'text-neutral-300'
              )}
            >
              {format(day, 'd')}
            </span>
            {hasEvents && (
              <div className="flex gap-0.5 mt-1">
                {dayEvents.slice(0, 3).map((_, idx) => (
                  <div
                    key={idx}
                    className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                  />
                ))}
              </div>
            )}
          </button>
        )
        day = addDays(day, 1)
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7 gap-1">
          {days}
        </div>
      )
      days = []
    }

    return <div className="space-y-1">{rows}</div>
  }

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4">
      {renderHeader()}
      {renderDaysOfWeek()}
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        renderCells()
      )}
    </div>
  )
}

export default CalendarView
